import { authHeaders } from './auth.js';
import { isRequired } from './consent.js';
/**
 * Dictation (ADR-0101): the visitor records their question, the RAG returns
 * the text, and it goes into the field for them to review and send. Nothing
 * is sent on their behalf, and the microphone is asked for only on the first
 * tap. Small on purpose: it ships in the one bundle every visitor downloads.
 */

export const MAX_SECONDS = 30;
// Transcribing 30 s of audio can take the API up to its own 25 s budget:
// wait longer than that, so the server answers first (a 503 or the text).
export const TRANSCRIBE_TIMEOUT_MS = 35000;
// The first one this browser records wins; Safari only does MP4.
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4'];
const SENDABLE = ['audio/webm', 'audio/ogg', 'audio/mp4'];

/** Whether this page can record at all: HTTPS, the APIs, and a page that allows the microphone. */
export function voiceSupported(win = globalThis) {
  if (!win.isSecureContext || !win.navigator?.mediaDevices?.getUserMedia || typeof win.MediaRecorder !== 'function') return false;
  // A site can forbid the microphone with Permissions-Policy (ADR-0101).
  const policy = win.document?.permissionsPolicy || win.document?.featurePolicy;
  if (policy && typeof policy.allowsFeature === 'function' && !policy.allowsFeature('microphone')) return false;
  return true;
}

export function pickMime(Recorder) {
  for (const type of MIME_TYPES) {
    if (typeof Recorder.isTypeSupported === 'function' && Recorder.isTypeSupported(type)) return type;
  }
  return '';
}

/** The type the API accepts, without parameters ("audio/webm;codecs=opus" → "audio/webm"). */
export function baseType(type) {
  const base = String(type || '').split(';')[0].trim().toLowerCase();
  return SENDABLE.includes(base) ? base : 'audio/webm';
}

/**
 * How loud the microphone is, 0–1, for the bars while recording. Without Web
 * Audio (or if it fails) the bars just stay still: never a reason not to record.
 */
function levelMeter(stream, win) {
  const Context = win.AudioContext || win.webkitAudioContext;
  if (typeof Context !== 'function') return { level: () => 0, close: () => {} };
  try {
    const context = new Context();
    const analyser = context.createAnalyser();
    analyser.fftSize = 256;
    context.createMediaStreamSource(stream).connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);
    return {
      level() {
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (const sample of samples) sum += ((sample - 128) / 128) ** 2;
        // Speech sits around 0.05–0.3 RMS: scaled so a normal voice fills the bars.
        return Math.min(1, Math.sqrt(sum / samples.length) * 4);
      },
      close: () => { context.close().catch(() => {}); },
    };
  } catch {
    return { level: () => 0, close: () => {} };
  }
}

/**
 * Starts recording. Rejects when the visitor (or the browser) refuses the
 * microphone. `done` resolves with the audio — or null when cancelled — and
 * the microphone is always released. `level()` is the voice's loudness now.
 */
export async function startRecording({ onTick = () => {}, maxSeconds = MAX_SECONDS, win = globalThis } = {}) {
  const stream = await win.navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = pickMime(win.MediaRecorder);
  let recorder;
  try {
    recorder = new win.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  } catch (error) {
    for (const track of stream.getTracks()) track.stop();
    throw error;
  }
  const meter = levelMeter(stream, win);
  const chunks = [];
  let cancelled = false;
  let left = maxSeconds;
  const stop = () => { if (recorder.state !== 'inactive') recorder.stop(); };
  recorder.addEventListener('dataavailable', (event) => { if (event.data && event.data.size) chunks.push(event.data); });
  const timer = setInterval(() => {
    left -= 1;
    onTick(Math.max(left, 0));
    if (left <= 0) stop();
  }, 1000);
  const done = new Promise((resolve) => {
    recorder.addEventListener('stop', () => {
      clearInterval(timer);
      meter.close();
      for (const track of stream.getTracks()) track.stop();
      resolve(cancelled || !chunks.length ? null : new Blob(chunks, { type: baseType(recorder.mimeType || mime) }));
    });
  });
  recorder.start();
  onTick(left);
  return { stop, cancel: () => { cancelled = true; stop(); }, done, level: meter.level };
}

/**
 * POST /api/transcribe. Returns one of:
 * - { kind: 'text', text } — possibly empty: nothing was understood
 * - { kind: 'session' } (401), { kind: 'consent' } (403 consent_required),
 *   { kind: 'limit' } (429), { kind: 'error' } — anything else
 */
export async function transcribe(apiBase, siteKey, session, audio, locale, timeoutMs = TRANSCRIBE_TIMEOUT_MS) {
  const url = new URL('/api/transcribe', apiBase);
  url.searchParams.set('site_key', siteKey);
  if (locale) url.searchParams.set('locale', locale);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      credentials: 'omit',
      signal: controller.signal,
      headers: { 'Content-Type': baseType(audio.type), ...authHeaders(), ...(session ? { 'X-Chat-Session': session } : {}) },
      body: audio,
    });
  } catch {
    return { kind: 'error' };
  } finally {
    clearTimeout(timer);
  }
  if (response.status === 401) return { kind: 'session' };
  if (response.status === 429) return { kind: 'limit' };
  const body = await response.json().catch(() => null);
  if (isRequired(response.status, body)) return { kind: 'consent' };
  if (!response.ok || !body) return { kind: 'error' };
  return { kind: 'text', text: typeof body.text === 'string' ? body.text.trim() : '' };
}
